import { useEffect, useRef } from "react"
import { HeatmapRowProps } from "./THzHeatmapRow.types"
import colormap from 'colormap'
import "./THzHeatmapRow.css"

const THzHeatmapRow = (props: HeatmapRowProps) => {
  
  const { 
    colours,
    vectors,
    scanInfo,
    colourPalette, 
    width, 
    pixelSize, 
    isSelected, 
    onSelect,
    scanNumber,
    minDataPoint,
    maxDataPoint,
    showOverlay
  } = props

  const highlightThreshold = 5
  const canvas = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const colourMap = colormap({
        colormap: colourPalette ?? 'magma',
        nshades: 256,
        format: 'hex',
        alpha: 1
    })

    const sampleToHexColour = (sample: number): string => {
      if (Math.ceil(sample) > maxDataPoint.value - highlightThreshold && maxDataPoint.show) { return "#1282A2"}
      if (Math.ceil(sample) < minDataPoint.value + highlightThreshold && minDataPoint.show && sample !== 0) { return "red"}

      const i = Math.ceil(Math.max(0, Math.min(255, sample)))    
      return colourMap[i]
    }

    const draw = (context: CanvasRenderingContext2D) => {
        
      if (canvas.current === null) { return }
      context.clearRect(0, 0, canvas.current.width, canvas.current.height);
      
      for (let column=0; column < colours.length; column++) {
        const scanRow = colours[column]
        const rowLength = scanRow.length        
  
        for (let row=0; row<rowLength; row++) {
          context.fillStyle = sampleToHexColour(scanRow[row])
          context.fillRect(
            Math.floor(row * pixelSize.width), 
            column * pixelSize.height,
            Math.ceil(pixelSize.width), 
            pixelSize.height
          )
        }
      }

      if (scanNumber === undefined || !showOverlay) { return }
      
      const maxY = ((scanNumber+1) * 4) - 1
      const minY = maxY - 3
      
      for (let i=0; i<vectors.length; i++) {
        const currentVector = vectors[i]
        if (maxY < currentVector[1]) { continue }
        if (minY > currentVector[1] + currentVector[3]) { continue }
        let y, h
  
        if (currentVector[1] < minY) {
          y = -1
        } else {
          y = (currentVector[1] - minY)
        }
  
        if (currentVector[1] + currentVector[3] > maxY) {
          h = 7
        } else {
          h = (currentVector[1] + currentVector[3] - minY)
        }
  
        context.strokeStyle = "#6ADCF2"
        context.lineWidth = 3
        context.strokeRect(
          currentVector[0] * pixelSize.width, 
          y * pixelSize.height,
          currentVector[2] * pixelSize.width, 
          h * pixelSize.height
        )
      }
    }
    
    if (canvas?.current === null) { return }
    const context = canvas.current.getContext('2d', { willReadFrequently: true })

    if (context === null) { return }
    draw(context)
  }, [colours, pixelSize.height, pixelSize.width, vectors, colourPalette, minDataPoint, maxDataPoint, scanNumber, showOverlay])

  return (
    <div className="heatmap-row-container">
      <canvas
        className={`heatmap-row ${isSelected ? 'heatmap-row-selected' : ''} ${onSelect ? 'heatmap-row-hoverable' : ''}`}
        onClick={onSelect ? () => onSelect() : () => {}}
        ref={canvas} 
        width={width}
        height={colours.length * pixelSize.height}
      />
      {scanInfo && (
        <div className="info-view">
          <p className="heatmap-info-text">{scanInfo}</p>
        </div>
      )}
    </div>
  )
}

THzHeatmapRow.defaultProps = {
  isSelected: false,
  showOverlay: false,
  maxDataPoint: { value: 0, show: false },
  minDataPoint: { value: 255, show: false },
  vectors: []
}

export default THzHeatmapRow